import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getApiBaseUrl, setStoredThreadId } from '../utils/chatIdentity';
import { getEmailAuthErrorMessage, getEmailValidationError, normalizeEmail } from '../utils/emailValidation';
import {
    getShopReferral,
    getShopReferralByCode,
    getShopReferralBySlug,
    requestShopReferralOtp,
    redeemShopReferral,
    redeemShopReferralByCode,
    redeemShopReferralBySlug,
    verifyShopReferralOtp,
} from '../utils/partnerShopApi';
import { DEFAULT_COUNTRY, getPhoneValidationError, normalizePhoneForOtp } from '../utils/phoneValidation';
import useOtpResendCooldown from '../hooks/useOtpResendCooldown';
import { getProfileNameError, normalizeProfileName } from '../utils/profileName';
import { buildLoginConsentPayload } from '../config/loginConsent';
import { acceptLoginConsent, isLoginConsentRequired } from '../utils/loginConsentApi';
import { LoginConsentStep, getLoginConsentHeading, getLoginConsentSubtitle } from './PhoneAuth';

const API_BASE_URL = getApiBaseUrl();
const AUTH_API_BASE_URL = `${API_BASE_URL}/auth`;

function normalizePhoneInputValue(value) {
    return String(value || '').replace(/\D/g, '');
}

function getLockedReferralPhoneError(value) {
    const digits = normalizePhoneInputValue(value);
    if (!digits) return 'Please enter a phone number.';
    if (digits.length < 8 || digits.length > 15) return 'Enter a valid phone number with country code.';
    return '';
}

function getReferralChannels(referral) {
    const channels = Array.isArray(referral?.available_channels) ? referral.available_channels : [];
    return channels.filter((channel) => channel === 'email' || channel === 'phone');
}

function getChannelLabel(channel) {
    return channel === 'email' ? 'Email' : 'Phone';
}

function openReferralChat(navigate, redeemed, { referralToken, referralCode, shopSlug, issueTitle }) {
    const threadId = redeemed?.thread?.thread_id;
    if (!/^[1-9]\d*$/.test(String(threadId || ''))) {
        throw new Error('Referral chat unavailable. Please contact the shop for help continuing this job.');
    }
    if (redeemed.identity) setStoredThreadId(redeemed.identity, threadId);
    navigate('/chat', {
        replace: true,
        state: {
            ...(!referralToken ? { q: redeemed.seed_message || issueTitle } : {}),
            selectThreadId: threadId,
            referralToken: referralToken || null,
            referralCode: referralCode || null,
            referralShopSlug: shopSlug || null,
        },
    });
}

export default function ShopReferralPage() {
    const { token = '', shopSlug = '', referralCode = '' } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const referralToken = token || searchParams.get('ref') || searchParams.get('token') || '';
    const [referral, setReferral] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [step, setStep] = useState('contact');
    const [authBusy, setAuthBusy] = useState(false);
    const [channel, setChannel] = useState('phone');
    const [phoneInput, setPhoneInput] = useState('');
    const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY);
    const [emailInput, setEmailInput] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [issueInput, setIssueInput] = useState('');
    const [pendingPhone, setPendingPhone] = useState('');
    const [pendingEmail, setPendingEmail] = useState('');
    const [otp, setOtp] = useState('');
    // #739: set when the verified driver still has to accept the current consent version.
    const [consentRequest, setConsentRequest] = useState(null);
    const [serviceConsentAccepted, setServiceConsentAccepted] = useState(false);
    const [marketingConsentOptIn, setMarketingConsentOptIn] = useState(false);
    const {
        secondsRemaining: resendSeconds,
        isCoolingDown: isResendCoolingDown,
        start: startResendCooldown,
        reset: resetResendCooldown,
    } = useOtpResendCooldown();

    useEffect(() => {
        const hasSensitiveParams = ['user', 'name', 'customer_name', 'phoneNumber', 'phone', 'email'].some((key) => searchParams.has(key));
        if (!hasSensitiveParams) return;
        const nextParams = new URLSearchParams();
        const refParam = searchParams.get('ref') || searchParams.get('token') || '';
        if (!token && refParam) nextParams.set('ref', refParam);
        const query = nextParams.toString();
        navigate(`${window.location.pathname}${query ? `?${query}` : ''}`, { replace: true });
    }, [navigate, searchParams, token]);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError('');
        const loadReferral = referralToken
            ? getShopReferral(referralToken)
            : (referralCode
                ? getShopReferralByCode(referralCode)
                : (shopSlug ? getShopReferralBySlug(shopSlug) : Promise.reject(new Error('Referral link is missing a shop.'))));
        loadReferral
            .then(async (data) => {
                if (cancelled) return;
                if (!data) throw new Error('This referral link is unavailable.');
                const nextReferral = data || {};
                const channels = getReferralChannels(nextReferral);
                setReferral(nextReferral);
                const preferred = channels.includes('email')
                    ? 'email'
                    : (channels[0] || (nextReferral?.customer_email ? 'email' : 'phone'));
                setChannel(preferred);
                setEmailInput(nextReferral?.customer_email || '');
                setPhoneInput(normalizePhoneInputValue(nextReferral?.customer_phone || ''));
                setCustomerName('');
                setIssueInput('');
                setStep('contact');
                setOtp('');
                setNotice('');
                setPendingEmail('');
                setPendingPhone('');
                resetResendCooldown();
                if (referralToken) {
                    try {
                        const redeemed = await redeemShopReferral(referralToken);
                        if (!cancelled) openReferralChat(navigate, redeemed, { referralToken, referralCode, shopSlug });
                    } catch (err) {
                        if (cancelled) return;
                        if (err.status === 401) return;
                        if (err.code === 'referral_account_mismatch') {
                            setNotice(err.message);
                            return;
                        }
                        throw err;
                    }
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setReferral(null);
                    setError(err?.message || 'This referral link is unavailable.');
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [navigate, referralCode, referralToken, resetResendCooldown, shopSlug]);

    const shopName = referral?.shop?.name || 'your shop';
    const issueTitle = referral?.issue_title || 'Vehicle issue';
    const typedIssue = issueInput.trim();
    const referralChannels = useMemo(() => getReferralChannels(referral), [referral]);
    const contactHints = referral?.contact_hints || {};
    const hasLockedReferralContact = Boolean(referralToken && referralChannels.length > 0);
    const isReusableReferral = Boolean(!referralToken && (referralCode || shopSlug));
    const displayedIssueTitle = isReusableReferral
        ? (typedIssue || 'Tell us what is happening with your vehicle')
        : issueTitle;
    const selectedContactHint = contactHints[channel] || getChannelLabel(channel).toLowerCase();
    const canSwitchChannel = hasLockedReferralContact
        ? referralChannels.length > 1
        : (!referral?.customer_email && !referral?.customer_phone);
    const showLockedContactDestination = hasLockedReferralContact && !(canSwitchChannel && step === 'contact');
    const isLockedEmail = Boolean(referral?.customer_email);
    const isLockedPhone = Boolean(referral?.customer_phone);

    async function requestOtp() {
        if (step === 'otp' && isResendCoolingDown) return;

        if (hasLockedReferralContact && !referralChannels.includes(channel)) {
            setError('Choose where to receive the verification code.');
            return;
        }
        let validationError = channel === 'email'
            ? (hasLockedReferralContact ? '' : getEmailValidationError(emailInput))
            : (hasLockedReferralContact ? '' : (isLockedPhone ? getLockedReferralPhoneError(phoneInput) : getPhoneValidationError(phoneInput, phoneCountry)));
        if (!validationError && isReusableReferral) {
            const nameError = getProfileNameError(customerName);
            if (nameError) validationError = nameError;
            else if (!typedIssue) validationError = 'Please describe what is happening with your vehicle.';
        }
        if (validationError) {
            setError(validationError);
            return;
        }

        const targetEmail = normalizeEmail(emailInput);
        const targetPhone = isLockedPhone ? normalizePhoneInputValue(phoneInput) : normalizePhoneForOtp(phoneInput, phoneCountry);

        setAuthBusy(true);
        setError('');
        setNotice('');
        try {
            if (hasLockedReferralContact) {
                await requestShopReferralOtp(referralToken, channel);
            } else {
                const endpoint = channel === 'email' ? '/request-email-otp' : '/request-otp';
                const body = channel === 'email' ? { email: targetEmail } : { phone: targetPhone };
                const response = await fetch(`${AUTH_API_BASE_URL}${endpoint}`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const fallback = 'Could not send a code.';
                    const detail = channel === 'email'
                        ? getEmailAuthErrorMessage(response, data, fallback)
                        : (data?.detail || data?.message || fallback);
                    throw new Error(detail);
                }
            }
            setPendingEmail(channel === 'email' ? targetEmail : '');
            setPendingPhone(channel === 'phone' ? targetPhone : '');
            setStep('otp');
            setNotice(`Code sent to ${hasLockedReferralContact ? selectedContactHint : (channel === 'email' ? 'your email' : 'your phone')}.`);
            startResendCooldown();
        } catch (err) {
            setError(err?.message || 'Could not send a code.');
        } finally {
            setAuthBusy(false);
        }
    }

    async function verifyAndContinue() {
        const code = otp.replace(/\D/g, '').slice(0, 6);
        if (code.length < 4) {
            setError('Enter the verification code.');
            return;
        }
        setAuthBusy(true);
        setError('');
        setNotice('');
        try {
            const webId = localStorage.getItem('dash_web_user_id') || '';
            let verified = null;
            if (hasLockedReferralContact) {
                verified = await verifyShopReferralOtp(referralToken, { channel, otp: code, webId });
            } else {
                if (isReusableReferral && !typedIssue) {
                    setStep('contact');
                    throw new Error('Please describe what is happening with your vehicle.');
                }
                const endpoint = channel === 'email' ? '/verify-email-otp' : '/verify-otp';
                const body = channel === 'email'
                    ? {
                        email: pendingEmail || normalizeEmail(emailInput),
                        otp: code,
                        web_id: webId,
                    }
                    : {
                        phone: pendingPhone || (
                            isLockedPhone ? normalizePhoneInputValue(phoneInput) : normalizePhoneForOtp(phoneInput, phoneCountry)
                        ),
                        otp: code,
                        web_id: webId,
                    };
                const response = await fetch(`${AUTH_API_BASE_URL}${endpoint}`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const detail = channel === 'email'
                        ? getEmailAuthErrorMessage(response, data, 'Invalid or expired code.')
                        : (data?.detail || data?.message || 'Invalid or expired code.');
                    throw new Error(detail);
                }
                verified = data;
            }
            if (isLoginConsentRequired(verified)) {
                // #739: no session yet. Both boxes start unchecked.
                setServiceConsentAccepted(false);
                setMarketingConsentOptIn(false);
                setConsentRequest({ token: verified.consent_token, reason: verified.consent_reason || 'missing' });
                setStep('consent');
                return;
            }
            await continueToReferralChat();
        } catch (err) {
            setError(err?.message || 'Could not verify this referral.');
        } finally {
            setAuthBusy(false);
        }
    }

    async function acceptConsentAndContinue() {
        if (!consentRequest?.token || !serviceConsentAccepted || authBusy) return;
        setAuthBusy(true);
        setError('');
        try {
            await acceptLoginConsent(AUTH_API_BASE_URL, {
                consentToken: consentRequest.token,
                consent: buildLoginConsentPayload({
                    serviceAccepted: serviceConsentAccepted,
                    marketingOptIn: marketingConsentOptIn,
                    sourcePage: `${window.location.pathname}${window.location.search || ''}`,
                }),
            });
            await continueToReferralChat();
        } catch (err) {
            setError(err?.message || 'Could not finish logging in.');
        } finally {
            setAuthBusy(false);
        }
    }

    // After sign-in (with or without the consent step): redeem the referral and open its chat.
    async function continueToReferralChat() {
        const reusableIssue = typedIssue;
        const reusablePayload = {
            customer_name: normalizeProfileName(customerName) || referral?.customer_name || '',
            customer_email: normalizeEmail(emailInput),
            customer_phone: phoneInput.trim() ? normalizePhoneForOtp(phoneInput, phoneCountry) : '',
            issue_title: reusableIssue,
            preliminary_issue: reusableIssue,
        };
        const redeemed = referralToken
            ? await redeemShopReferral(referralToken)
            : (referralCode
                ? await redeemShopReferralByCode(referralCode, reusablePayload)
                : await redeemShopReferralBySlug(shopSlug, reusablePayload));
        openReferralChat(navigate, redeemed, { referralToken, referralCode, shopSlug, issueTitle: displayedIssueTitle });
    }

    const detailRows = useMemo(() => [
        referralToken && referral?.customer_name ? ['Driver', referral.customer_name] : null,
        referral?.appointment_date ? ['Appointment', referral.appointment_date] : null,
    ].filter(Boolean), [referral, referralToken]);

    if (isLoading) {
        return <main className="grid min-h-screen place-items-center bg-[#F9F9F9] px-5 text-sm font-bold text-[#414141]">Loading referral...</main>;
    }

    if (error && !referral) {
        return (
            <main className="grid min-h-screen place-items-center bg-[#F9F9F9] px-5">
                <div className="w-full max-w-md rounded-2xl border border-[#ffd2d2] bg-white p-5 shadow-sm">
                    <p className="text-lg font-black text-[#b42318]">Referral unavailable</p>
                    <p className="mt-2 text-sm font-semibold text-[#414141]">{error}</p>
                    <p className="mt-3 rounded-xl bg-[#fff8e8] px-3 py-2 text-xs font-bold leading-relaxed text-[#936000]">
                        Please request a new referral link from the shop and open that new link to continue.
                    </p>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-[#F9F9F9] px-5 py-8 text-[#414141]">
            <div className="mx-auto flex w-full max-w-xl flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#D81F26]">CarFido</p>
                        <h1 className="mt-1 text-2xl font-black tracking-tight text-black">Verify to start your intake</h1>
                    </div>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-black text-sm font-black text-white shadow-sm">
                        D
                    </div>
                </div>

                <div className="overflow-hidden rounded-[24px] border border-[#d9d9d9] bg-white shadow-[0_18px_48px_rgba(34,34,34,0.08)]">
                    <div className="border-b border-[#d9d9d9] px-5 py-5">
                        <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#414141]">Referred by</p>
                        <h2 className="mt-1 text-xl font-black text-[#414141]">{shopName}</h2>
                        <p className="mt-2 text-sm font-bold leading-relaxed text-[#414141]">{displayedIssueTitle}</p>
                    </div>

                    <div className="space-y-4 px-5 py-5">
                    {referral?.preliminary_issue && (
                        <div className="rounded-xl border border-[#f7d58f] bg-[#fff8e8] px-4 py-3">
                            <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#936000]">Issue details</p>
                            <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">{referral.preliminary_issue}</p>
                        </div>
                    )}

                    {detailRows.length > 0 && (
                        <div className="grid gap-2">
                            {detailRows.map(([label, value]) => (
                                <div key={label} className="flex items-center justify-between gap-3 rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-2">
                                    <span className="text-xs font-black uppercase tracking-[0.08em] text-[#414141]">{label}</span>
                                    <span className="text-sm font-black text-[#414141]">{value}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="rounded-2xl border border-[#d9d9d9] bg-white px-4 py-4">
                        <style>{`
                            .referral-phone-container { width: 100% !important; }
                            .referral-phone-container .form-control {
                                width: 100% !important;
                                height: 44px !important;
                                padding-left: 58px !important;
                                padding-right: 12px !important;
                                border: 1px solid #d9d9d9 !important;
                                border-radius: 12px !important;
                                background: #ffffff !important;
                                color: #414141 !important;
                                font-family: inherit !important;
                                font-size: 14px !important;
                                font-weight: 700 !important;
                                outline: none !important;
                                box-shadow: none !important;
                            }
                            .referral-phone-container .form-control:focus {
                                border-color: #D81F26 !important;
                                box-shadow: none !important;
                            }
                            .referral-phone-container .form-control:disabled {
                                background: #F9F9F9 !important;
                                color: #414141 !important;
                            }
                            .referral-phone-container .flag-dropdown {
                                border: none !important;
                                border-right: 1px solid #d9d9d9 !important;
                                border-radius: 12px 0 0 12px !important;
                                background: transparent !important;
                            }
                            .referral-phone-container .selected-flag {
                                border-radius: 12px 0 0 12px !important;
                                padding-left: 10px !important;
                            }
                            .referral-phone-dropdown {
                                max-height: 260px !important;
                                border-radius: 16px !important;
                                border: 1px solid rgba(20, 20, 20, 0.12) !important;
                                box-shadow: 0 18px 48px rgba(20, 20, 20, 0.16) !important;
                                overflow-x: hidden !important;
                            }
                            .referral-phone-dropdown .search {
                                padding: 8px !important;
                                background: #ffffff !important;
                            }
                            .referral-phone-dropdown .search-box {
                                width: 100% !important;
                                height: 36px !important;
                                margin: 0 !important;
                                border: 1px solid #d9d9d9 !important;
                                border-radius: 10px !important;
                                font-family: inherit !important;
                                font-size: 13px !important;
                                font-weight: 600 !important;
                            }
                        `}</style>
                        <p className="text-sm font-black text-[#414141]">Verify to start your intake</p>
                        <p className="mt-1 text-xs font-semibold leading-relaxed text-[#414141]">
                            CarFido will open a new chat for this issue. Nothing is shared with {shopName} until you approve it after the diagnostic report.
                        </p>

                        {step === 'consent' ? (
                            <div className="mt-3" data-testid="referral-consent-step">
                                <p className="text-lg font-black text-[#071426]">{getLoginConsentHeading(consentRequest?.reason)}</p>
                                <p className="mt-1 text-sm font-semibold leading-relaxed text-[#5f728a]">
                                    {getLoginConsentSubtitle(consentRequest?.reason)}
                                </p>
                                <LoginConsentStep
                                    className="mt-4 space-y-4 font-brand text-fg"
                                    serviceConsentAccepted={serviceConsentAccepted}
                                    marketingConsentOptIn={marketingConsentOptIn}
                                    onServiceConsentChange={setServiceConsentAccepted}
                                    onMarketingConsentChange={setMarketingConsentOptIn}
                                    onAccept={acceptConsentAndContinue}
                                    authBusy={authBusy}
                                    authError={error}
                                />
                            </div>
                        ) : (
                        <>
                        {isReusableReferral && step === 'contact' && (
                            <div className="mt-3 space-y-3">
                                <input
                                    value={customerName}
                                    onChange={(event) => { setCustomerName(event.target.value); if (error) setError(''); }}
                                    placeholder="Your name"
                                    type="text"
                                    autoComplete="name"
                                    className="h-11 w-full rounded-xl border border-[#d9d9d9] px-3 text-sm font-bold outline-none focus:border-[#D81F26]"
                                />
                                <div className="grid grid-cols-2 rounded-xl bg-[#f7f7f7] p-1 text-xs font-black">
                                    {['phone', 'email'].map((option) => (
                                        <button
                                            key={option}
                                            type="button"
                                            onClick={() => { setChannel(option); setError(''); resetResendCooldown(); }}
                                            className={`rounded-lg px-3 py-2 ${channel === option ? 'bg-[#414141] text-white shadow-sm' : 'text-[#414141]'}`}
                                        >
                                            {getChannelLabel(option)}
                                        </button>
                                    ))}
                                </div>
                                {channel === 'email' ? (
                                    <input
                                        value={emailInput}
                                        onChange={(event) => { setEmailInput(event.target.value); if (error) setError(''); }}
                                        placeholder="you@example.com"
                                        type="email"
                                        inputMode="email"
                                        autoComplete="email"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        className="h-11 w-full rounded-xl border border-[#d9d9d9] px-3 text-sm font-bold outline-none focus:border-[#D81F26]"
                                    />
                                ) : (
                                    <PhoneInput
                                        country={DEFAULT_COUNTRY.countryCode}
                                        value={phoneInput}
                                        onChange={(value, country) => {
                                            setPhoneInput(normalizePhoneInputValue(value));
                                            if (country?.dialCode) setPhoneCountry(country);
                                            if (error) setError('');
                                        }}
                                        placeholder="908 242 3514"
                                        enableSearch={true}
                                        searchPlaceholder="Search country or code…"
                                        disableSearchIcon={true}
                                        disableCountryGuess={true}
                                        disableInitialCountryGuess={false}
                                        countryCodeEditable={false}
                                        containerClass="referral-phone-container"
                                        dropdownClass="referral-phone-dropdown"
                                        inputProps={{ id: 'referral-phone-input', autoComplete: 'tel' }}
                                    />
                                )}
                                <textarea
                                    value={issueInput}
                                    onChange={(event) => { setIssueInput(event.target.value); if (error) setError(''); }}
                                    placeholder="Briefly describe what is happening with your vehicle"
                                    required
                                    aria-label="Describe the vehicle issue"
                                    rows={3}
                                    className="min-h-[92px] w-full resize-y rounded-xl border border-[#d9d9d9] px-3 py-2.5 text-sm font-bold outline-none focus:border-[#D81F26]"
                                />
                            </div>
                        )}

	                        {canSwitchChannel && step === 'contact' && !isReusableReferral && (
	                            <div className="mt-3 grid grid-cols-2 rounded-xl bg-[#f7f7f7] p-1 text-xs font-black">
                                {(hasLockedReferralContact ? referralChannels : ['phone', 'email']).map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => { setChannel(option); setError(''); resetResendCooldown(); }}
                                        className={`rounded-lg px-3 py-2 ${channel === option ? 'bg-[#414141] text-white shadow-sm' : 'text-[#414141]'}`}
                                    >
                                        {hasLockedReferralContact ? `${getChannelLabel(option)} ${contactHints[option] || ''}`.trim() : getChannelLabel(option)}
                                    </button>
                                ))}
                            </div>
                        )}

                        {isReusableReferral ? null : showLockedContactDestination ? (
                            <div className="mt-3 rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-3">
                                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Code destination</p>
                                <p className="mt-1 text-sm font-black text-[#414141]">{getChannelLabel(channel)} {selectedContactHint}</p>
                            </div>
                        ) : hasLockedReferralContact ? null : channel === 'email' ? (
                            <input
                                value={emailInput}
                                onChange={(event) => { setEmailInput(event.target.value); if (error) setError(''); }}
                                disabled={isLockedEmail || step === 'otp'}
                                placeholder="you@example.com"
                                type="email"
                                inputMode="email"
                                autoComplete="email"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                className="mt-3 h-11 w-full rounded-xl border border-[#d9d9d9] px-3 text-sm font-bold outline-none focus:border-[#D81F26] disabled:bg-[#F9F9F9] disabled:text-[#414141]"
                            />
                        ) : (
                            <div className="mt-3">
                                <PhoneInput
                                    country={DEFAULT_COUNTRY.countryCode}
                                    value={phoneInput}
                                    onChange={(value, country) => {
                                        setPhoneInput(normalizePhoneInputValue(value));
                                        if (country?.dialCode) setPhoneCountry(country);
                                        if (error) setError('');
                                    }}
                                    disabled={isLockedPhone || step === 'otp'}
                                    placeholder="908 242 3514"
                                    enableSearch={true}
                                    searchPlaceholder="Search country or code…"
                                    disableSearchIcon={true}
                                    disableCountryGuess={true}
                                    disableInitialCountryGuess={false}
                                    countryCodeEditable={false}
                                    containerClass="referral-phone-container"
                                    dropdownClass="referral-phone-dropdown"
                                    inputProps={{ id: 'referral-phone-input', autoComplete: 'tel' }}
                                />
                            </div>
                        )}

                        {step === 'otp' && (
                            <div className="mt-3 space-y-2">
                                <input
                                    value={otp}
                                    onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="Verification code"
                                    inputMode="numeric"
                                    className="h-11 w-full rounded-xl border border-[#d9d9d9] px-3 text-sm font-bold outline-none focus:border-[#D81F26]"
                                />
                                <button
                                    type="button"
                                    onClick={requestOtp}
                                    disabled={authBusy || isResendCoolingDown}
                                    aria-label={resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : 'Resend code'}
                                    className="text-xs font-black text-[#D81F26] transition hover:text-[#B8181E] disabled:cursor-not-allowed disabled:text-[#b0b0b0]"
                                >
                                    {resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : 'Resend code'}
                                </button>
                            </div>
                        )}

                        {notice && <p className="mt-2 text-xs font-bold text-[#087a43]">{notice}</p>}
                        {error && <p className="mt-2 text-xs font-bold text-[#b42318]">{error}</p>}

                        <button
                            type="button"
                            onClick={step === 'otp' ? verifyAndContinue : requestOtp}
                            disabled={authBusy}
                            className="mt-4 h-11 w-full rounded-xl bg-[#D81F26] px-4 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:cursor-wait disabled:opacity-70"
                        >
                            {authBusy ? 'Please wait' : step === 'otp' ? 'Verify & Open Chat' : `Send ${getChannelLabel(channel)} Code`}
                        </button>
                        </>
                        )}
                    </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
